import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { EyeIcon, MoreIcon } from '../icons/Icons';
import { Tooltip } from '../Tooltip/Tooltip';
import './detail-panel.css';

export interface DetailPanelProperty {
  label: string;
  value: ReactNode;
}

interface DetailPanelProps {
  icon?: ReactNode;
  title: string;
  /** Overrides the plain `title` text in the header — used for inline
   * rename editing (see MeasurementDetailPanel's more-menu "Rename"). */
  titleContent?: ReactNode;
  /** Creator/date line etc, rendered right under the header/badge/media. */
  meta?: ReactNode;
  /** e.g. a "TEST FIXTURE" badge. */
  badge?: ReactNode;
  /** e.g. a thumbnail button (photo panel only). */
  media?: ReactNode;
  onClose: () => void;
  /** Only rendered when provided — not every entity has a meaningful
   * per-item visibility toggle (Field Photos pins don't; measurements do). */
  visible?: boolean;
  onToggleVisible?: () => void;
  /** Real dropdown content for the "More" ("...") button, e.g. Rename/
   * Delete/Duplicate. Render-prop so menu items can close the menu after
   * acting. When omitted, "More" stays a plain "not available yet" stub
   * (Field Photos' panel doesn't pass this). */
  moreMenu?: (close: () => void) => ReactNode;
  /** "Access" section content — decorative/stub, see callers. */
  access?: ReactNode;
  properties: DetailPanelProperty[];
  description: string;
  onDescriptionChange: (value: string) => void;
  /** Extra content inserted after Description, before the stub sections. */
  children?: ReactNode;
}

/**
 * Shared chrome for a map-entity detail panel: header (icon, title, more/
 * eye/close actions), optional badge/media/meta, a Properties block, an
 * editable Description, and stub Attachments/Timeline Dates/Comments
 * sections. Originally built for Field Photos (PhotoDetailPanel) and
 * generalized here so MeasurementDetailPanel reuses the exact same shell
 * rather than duplicating this markup — see the plan message that asked
 * for this extraction.
 */
export function DetailPanel({
  icon,
  title,
  titleContent,
  meta,
  badge,
  media,
  onClose,
  visible,
  onToggleVisible,
  moreMenu,
  access,
  properties,
  description,
  onDescriptionChange,
  children,
}: DetailPanelProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    function onDocPointerDown(e: PointerEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMoreOpen(false);
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [moreOpen]);

  return (
    <div className="detail-panel">
      <div className="detail-panel-header">
        <div className="detail-panel-header-title">
          {icon && <span className="detail-panel-header-icon">{icon}</span>}
          {titleContent ?? <span>{title}</span>}
        </div>
        <div className="detail-panel-header-actions">
          {moreMenu ? (
            <div className="detail-panel-more" ref={moreRef}>
              <Tooltip heading="More" description="Rename, duplicate, set colour, delete…">
                <button
                  type="button"
                  className="detail-panel-icon-btn"
                  aria-label="More options"
                  aria-expanded={moreOpen}
                  aria-haspopup="menu"
                  onClick={() => setMoreOpen((v) => !v)}
                >
                  <MoreIcon />
                </button>
              </Tooltip>
              {moreOpen && moreMenu(() => setMoreOpen(false))}
            </div>
          ) : (
            <Tooltip heading="More" description="Not available yet.">
              <button type="button" className="detail-panel-icon-btn" aria-label="More options">
                <MoreIcon />
              </button>
            </Tooltip>
          )}
          {onToggleVisible && (
            <Tooltip
              heading={visible ? 'Hide' : 'Show'}
              description={`Toggle "${title}" on the map.`}
            >
              <button
                type="button"
                className="detail-panel-icon-btn"
                onClick={onToggleVisible}
                aria-pressed={visible}
                aria-label={visible ? `Hide ${title}` : `Show ${title}`}
              >
                <EyeIcon visible={!!visible} />
              </button>
            </Tooltip>
          )}
          <button type="button" className="detail-panel-icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
      </div>

      {badge}
      {media}
      {meta && <div className="detail-panel-meta">{meta}</div>}

      {access && (
        <section className="detail-panel-section">
          <h4>Access</h4>
          {access}
        </section>
      )}

      <section className="detail-panel-section">
        <h4>Properties</h4>
        <dl className="detail-panel-props">
          {properties.map((p) => (
            <Fragment key={p.label}>
              <dt>{p.label}</dt>
              <dd>{p.value}</dd>
            </Fragment>
          ))}
        </dl>
      </section>

      <section className="detail-panel-section">
        <h4>Description</h4>
        <textarea
          className="detail-panel-description"
          value={description}
          placeholder="Add a description…"
          onChange={(e) => onDescriptionChange(e.target.value)}
        />
      </section>

      {children}

      <section className="detail-panel-section detail-panel-stub">
        <h4>Attachments</h4>
        <p>No attachments yet.</p>
      </section>
      <section className="detail-panel-section detail-panel-stub">
        <h4>Timeline Dates</h4>
        <p>No timeline dates set.</p>
      </section>
      <section className="detail-panel-section detail-panel-stub">
        <h4>Comments</h4>
        <p>No comments yet.</p>
      </section>
    </div>
  );
}
